<template>
    <div class="vrm-form__fieldset-item vrm-axis-configuration-field">
        <div>
            <div>
                <label class="sg-textfield-label" :class="{ 'sg-required': required }">
                    <span class="sg-textfield-label-caption">
                        {{
                            $t('form.axis_configuration.y_axis_minimum', {
                                i18nFallback: 'Y-axis minimum',
                            })
                        }}
                    </span>
                </label>
                <input
                    ref="inputMin"
                    v-model="minValue"
                    type="number"
                    class="sg-element"
                    step="0.001"
                    :disabled="disabled"
                    :class="{ 'sg-error': error }"
                    data-cy="widget-axis-minimum"
                    @blur="validate"
                />
            </div>
            <div>
                <label class="sg-textfield-label" :class="{ 'sg-required': required }">
                    <span class="sg-textfield-label-caption">
                        {{
                            $t('form.axis_configuration.y_axis_maximum', {
                                i18nFallback: 'Y-axis maximum',
                            })
                        }}
                    </span>
                </label>
                <input
                    ref="inputMax"
                    v-model="maxValue"
                    type="number"
                    class="sg-element"
                    step="0.001"
                    :disabled="disabled"
                    :class="{ 'sg-error': error }"
                    data-cy="widget-axis-maximum"
                    @blur="validate"
                />
            </div>
            <div>
                <label class="sg-textfield-label" :class="{ 'sg-required': required }">
                    <span class="sg-textfield-label-caption">
                        {{
                            $t('form.axis_configuration.y_axis_intervals', {
                                i18nFallback: 'Y-axis intervals',
                            })
                        }}
                    </span>
                </label>
                <input
                    ref="inputInterval"
                    v-model="intervalValue"
                    type="number"
                    class="sg-element"
                    :disabled="disabled"
                    :class="{ 'sg-error': error }"
                    data-cy="widget-axis-intervals"
                    @blur="validate"
                />
            </div>
        </div>
        <div v-if="errorMessage" class="vrm-form_input-errors">
            <span class="sg-input-error">{{ errorMessage }}</span>
        </div>
    </div>
</template>
<style scoped lang="scss"></style>
<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';

@Component({ inheritAttrs: false })
@AngularLocaleBundle('form')
export default class VrmAxisConfigurationField extends Vue {
    @Prop({ type: Boolean, default: false })
    disabled;
    @Prop({ type: Boolean, default: false })
    required;
    @Prop({ type: Object, default: () => {} })
    value;

    valuePostedThisTick = false;
    errorMessage = false;
    touched = {};
    newValue = {};
    checks = [
        {
            check: value =>
                this.required || (value.min != null && value.max != null && value.interval != null),
            error: this.$t('form.axis_configuration.errors.required'),
        },
        {
            check: value => +value.interval > 0,
            error: this.$t('form.axis_configuration.errors.interval_too_small'),
        },
        {
            check: value => +value.interval <= 30,
            error: this.$t('form.axis_configuration.errors.interval_too_big'),
        },
        {
            check: value => +value.min >= -500000,
            error: this.$t('form.axis_configuration.errors.min_too_small'),
        },
        {
            check: value => +value.max <= 500000,
            error: this.$t('form.axis_configuration.errors.max_too_big'),
        },
        {
            check: value => +value.min < +value.max,
            error: this.$t('form.axis_configuration.errors.max_smaller_than_min'),
        },
    ];

    mounted() {
        this.newValue = _.clone(this.value);
    }

    get minValue() {
        return this.newValue.min;
    }

    set minValue(newValue) {
        this.setValue('min', newValue);
    }

    get maxValue() {
        return this.newValue.max;
    }

    set maxValue(newValue) {
        this.setValue('max', newValue);
    }

    get intervalValue() {
        return this.newValue.interval;
    }

    set intervalValue(newValue) {
        this.setValue('interval', newValue ? Math.round(newValue) : newValue);
    }

    setValue(field, newValue) {
        this.newValue[field] = newValue;
        this.touched[field] = true;
        this.valuePostedThisTick = true;
        if (this.isValid()) {
            this.$emit('input', _.clone(this.newValue));
        }
        this.$nextTick(() => {
            this.valuePostedThisTick = false;
        });
    }

    validate() {
        if (
            (this.newValue.min || this.touched.min) &&
            (this.newValue.max || this.touched.max) &&
            (this.newValue.interval || this.touched.interval)
        ) {
            this.vrmValidate();
        }
    }

    vrmValidate(setError = true) {
        if (setError) this.errorMessage = false;
        for (let i = 0; i < this.checks.length; i++) {
            const { check, error } = this.checks[i];
            if (!check(this.newValue)) {
                if (setError) this.errorMessage = error;
                return false;
            }
        }
        return true;
    }

    isValid() {
        return this.vrmValidate(false);
    }

    get error() {
        return (this.touched.min || this.touched.max || this.touched.interval) && this.errorMessage;
    }
}
</script>
